import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import api from '@/lib/api';
import {
  Plus, Pencil, Trash2, Copy, Percent, Euro, Tag, 
  Download, Gift, CreditCard, Crown, Search, TicketCheck,
  CalendarDays, Users, Eye, Zap, Cake, Heart, RotateCcw,
  Save, Play, History
} from 'lucide-react';

export default function AdminDiscountCodes() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [cardDialog, setCardDialog] = useState(null);
  const [cardLoading, setCardLoading] = useState(false);
  const [detailDialog, setDetailDialog] = useState(null);

  // Auto-discount state
  const [autoSettings, setAutoSettings] = useState({
    birthday_enabled: false, birthday_type: 'percentage', birthday_value: 10, birthday_message: 'Alles Gute zum Geburtstag! Als Geschenk erhalten Sie einen exklusiven Rabatt.',
    loyalty_enabled: false, loyalty_type: 'percentage', loyalty_value: 15, loyalty_threshold: 5, loyalty_message: 'Vielen Dank für Ihre Treue! Als Dankeschön erhalten Sie einen speziellen Rabatt.',
    reactivation_enabled: false, reactivation_type: 'percentage', reactivation_value: 20, reactivation_months: 6, reactivation_message: 'Wir vermissen Sie! Hier ist ein spezieller Rabatt für Ihren nächsten Besuch.'
  });
  const [autoSettingsLoading, setAutoSettingsLoading] = useState(true);
  const [savingAutoSettings, setSavingAutoSettings] = useState(false);
  const [autoLog, setAutoLog] = useState([]);
  const [triggeringCheck, setTriggeringCheck] = useState(false);

  const [form, setForm] = useState({
    code: '', name: '', type: 'percentage', value: 0,
    usage_type: 'multi', max_uses: null,
    valid_from: '', valid_until: '',
    min_order_value: 0, active: true
  });

  const fetchCodes = async () => {
    try {
      const res = await api.get('/admin/discount-codes');
      setCodes(res.data);
    } catch (err) {
      toast.error('Fehler beim Laden der Rabattcodes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCodes(); fetchAutoSettings(); }, []);

  const fetchAutoSettings = async () => {
    try {
      const [settingsRes, logRes] = await Promise.all([
        api.get('/admin/auto-discount-settings'),
        api.get('/admin/auto-discount-log')
      ]);
      if (settingsRes.data) {
        setAutoSettings(prev => ({ ...prev, ...settingsRes.data }));
      }
      setAutoLog(logRes.data || []);
    } catch (err) {
      // Settings may not exist yet
    } finally {
      setAutoSettingsLoading(false);
    }
  };

  const saveAutoSettings = async () => {
    setSavingAutoSettings(true);
    try {
      await api.put('/admin/auto-discount-settings', autoSettings);
      toast.success('Automatisierung gespeichert');
    } catch (err) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSavingAutoSettings(false);
    }
  };

  const triggerManualCheck = async () => {
    setTriggeringCheck(true);
    try {
      const res = await api.post('/admin/auto-discount/trigger-check');
      const sent = res.data?.sent || {};
      const total = (sent.birthday || 0) + (sent.loyalty || 0) + (sent.reactivation || 0);
      if (total > 0) {
        toast.success(`${total} Rabatt-E-Mail(s) gesendet: ${sent.birthday || 0} Geburtstag, ${sent.loyalty || 0} Treue, ${sent.reactivation || 0} Reaktivierung`);
      } else {
        toast.info('Keine ausstehenden Rabatt-E-Mails gefunden');
      }
      fetchAutoSettings();
      fetchCodes();
    } catch (err) {
      toast.error('Fehler beim Prüfen');
    } finally {
      setTriggeringCheck(false);
    }
  };

  const resetForm = () => {
    setForm({ code: '', name: '', type: 'percentage', value: 0, usage_type: 'multi', max_uses: null, valid_from: '', valid_until: '', min_order_value: 0, active: true });
    setEditingCode(null);
  };

  const openCreate = () => { resetForm(); setShowDialog(true); };

  const openEdit = (c) => {
    setEditingCode(c);
    setForm({
      code: c.code, name: c.name || '', type: c.type, value: c.value,
      usage_type: c.usage_type, max_uses: c.max_uses,
      valid_from: c.valid_from || '', valid_until: c.valid_until || '',
      min_order_value: c.min_order_value || 0, active: c.active
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.code.trim()) { toast.error('Code ist erforderlich'); return; }
    if (form.value <= 0) { toast.error('Wert muss größer als 0 sein'); return; }
    try {
      const payload = { ...form, max_uses: form.max_uses || null, valid_from: form.valid_from || null, valid_until: form.valid_until || null };
      if (editingCode) {
        await api.put(`/admin/discount-codes/${editingCode.id}`, payload);
        toast.success('Rabattcode aktualisiert');
      } else {
        await api.post('/admin/discount-codes', payload);
        toast.success('Rabattcode erstellt');
      }
      setShowDialog(false);
      resetForm();
      fetchCodes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Rabattcode wirklich löschen?')) return;
    try {
      await api.delete(`/admin/discount-codes/${id}`);
      toast.success('Rabattcode gelöscht');
      fetchCodes();
    } catch (err) {
      toast.error('Fehler beim Löschen');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`"${code}" kopiert`);
  };

  const generateCard = async (codeId, cardType) => {
    setCardLoading(true);
    try {
      const res = await api.post(`/admin/discount-codes/${codeId}/generate-card`, { card_type: cardType });
      const pdf = res.data.pdf_base64;
      const blob = new Blob([Uint8Array.from(atob(pdf), c => c.charCodeAt(0))], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${cardType === 'geschenk' ? 'Geschenkgutschein' : cardType === 'rabatt' ? 'Rabattkarte' : 'VIP-Karte'}_${res.data.code}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Karte erstellt');
    } catch (err) {
      toast.error('Fehler beim Erstellen der Karte');
    } finally {
      setCardLoading(false);
      setCardDialog(null);
    }
  };

  const filteredCodes = codes.filter(c =>
    c.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (c.name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const stats = {
    total: codes.length,
    active: codes.filter(c => c.active).length,
    totalRedeemed: codes.reduce((sum, c) => sum + (c.used_count || 0), 0)
  };

  return (
    <div className="space-y-6" data-testid="discount-codes-page">
      <Tabs defaultValue="codes" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="codes" className="gap-2" data-testid="tab-codes">
            <Tag className="w-4 h-4" /> Rabattcodes
          </TabsTrigger>
          <TabsTrigger value="auto" className="gap-2" data-testid="tab-auto">
            <Zap className="w-4 h-4" /> Automatisierung
          </TabsTrigger>
        </TabsList>

        <TabsContent value="codes" className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#9B1B30]/10"><Tag className="w-5 h-5 text-[#9B1B30]" /></div>
              <div>
                <p className="text-sm text-gray-500">Gesamt</p>
                <p className="text-2xl font-bold" data-testid="stat-total">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100"><TicketCheck className="w-5 h-5 text-green-600" /></div>
              <div>
                <p className="text-sm text-gray-500">Aktiv</p>
                <p className="text-2xl font-bold text-green-600" data-testid="stat-active">{stats.active}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100"><Users className="w-5 h-5 text-blue-600" /></div>
              <div>
                <p className="text-sm text-gray-500">Eingelöst</p>
                <p className="text-2xl font-bold text-blue-600" data-testid="stat-redeemed">{stats.totalRedeemed}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Rabattcode suchen..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="pl-9"
            data-testid="search-discount-codes"
          />
        </div>
        <Button onClick={openCreate} className="bg-[#9B1B30] hover:bg-[#7D1526]" data-testid="create-discount-btn">
          <Plus className="w-4 h-4 mr-2" /> Neuer Rabattcode
        </Button>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">Laden...</div>
      ) : filteredCodes.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-gray-500">
            {searchTerm ? 'Kein Rabattcode gefunden' : 'Noch keine Rabattcodes erstellt'}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {filteredCodes.map(c => (
            <Card key={c.id} className={`transition-all hover:shadow-md ${!c.active ? 'opacity-60' : ''}`}>
              <CardContent className="py-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 justify-between">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className={`p-3 rounded-xl ${c.type === 'percentage' ? 'bg-purple-100' : 'bg-emerald-100'}`}>
                      {c.type === 'percentage' ? <Percent className="w-5 h-5 text-purple-600" /> : <Euro className="w-5 h-5 text-emerald-600" />}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono font-bold text-lg tracking-wider" data-testid={`code-${c.code}`}>{c.code}</span>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => copyCode(c.code)}>
                          <Copy className="w-3 h-3" />
                        </Button>
                        <Badge variant={c.active ? 'default' : 'secondary'} className={c.active ? 'bg-green-100 text-green-700 hover:bg-green-100' : ''}>
                          {c.active ? 'Aktiv' : 'Inaktiv'}
                        </Badge>
                        <Badge variant="outline">
                          {c.usage_type === 'single' ? 'Einmalig' : 'Mehrfach'}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-500 mt-0.5">
                        {c.name || 'Kein Name'} — <span className="font-semibold text-gray-700">
                          {c.type === 'percentage' ? `${c.value}%` : `${c.value.toFixed(2)} EUR`}
                        </span>
                        {c.used_count > 0 && <span className="ml-2 text-blue-600">({c.used_count}x eingelöst)</span>}
                        {c.max_uses && <span className="ml-1 text-gray-400">/ max {c.max_uses}</span>}
                      </p>
                      {(c.valid_from || c.valid_until) && (
                        <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
                          <CalendarDays className="w-3 h-3" />
                          {c.valid_from && `Ab ${c.valid_from}`}
                          {c.valid_from && c.valid_until && ' — '}
                          {c.valid_until && `Bis ${c.valid_until}`}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button variant="ghost" size="sm" onClick={() => setDetailDialog(c)} title="Details" data-testid={`view-${c.code}`}>
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setCardDialog(c)} title="Karte erstellen" data-testid={`card-${c.code}`}>
                      <Gift className="w-4 h-4 text-[#9B1B30]" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(c)} data-testid={`edit-${c.code}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(c.id)} className="text-red-500 hover:text-red-700" data-testid={`delete-${c.code}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      </TabsContent>

      {/* Automatisierung Tab */}
      <TabsContent value="auto" className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <div>
            <h2 className="text-lg font-bold">Automatische Rabatt-E-Mails</h2>
            <p className="text-sm text-gray-500">Rabattcodes automatisch an Kunden senden</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={triggerManualCheck} disabled={triggeringCheck} data-testid="trigger-check-btn">
              <Play className="w-4 h-4 mr-2" />
              {triggeringCheck ? 'Prüfe...' : 'Jetzt prüfen'}
            </Button>
            <Button onClick={saveAutoSettings} disabled={savingAutoSettings} className="bg-[#9B1B30] hover:bg-[#7D1526]" data-testid="save-auto-settings-btn">
              <Save className="w-4 h-4 mr-2" />
              {savingAutoSettings ? 'Speichert...' : 'Speichern'}
            </Button>
          </div>
        </div>

        {/* Birthday Discount */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Cake className="w-5 h-5 text-pink-500" />
                Geburtstags-Rabatt
              </CardTitle>
              <Switch checked={autoSettings.birthday_enabled} onCheckedChange={v => setAutoSettings(s => ({...s, birthday_enabled: v}))} data-testid="birthday-enabled" />
            </div>
          </CardHeader>
          {autoSettings.birthday_enabled && (
            <CardContent className="space-y-3 pt-0">
              <p className="text-sm text-gray-500">Am Geburtstag des Kunden wird automatisch ein persönlicher Rabattcode per E-Mail gesendet.</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Typ</Label>
                  <Select value={autoSettings.birthday_type} onValueChange={v => setAutoSettings(s => ({...s, birthday_type: v}))}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">Prozent (%)</SelectItem>
                      <SelectItem value="fixed">Festbetrag (EUR)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Wert</Label>
                  <Input type="number" min="0" step="0.01" value={autoSettings.birthday_value} onChange={e => setAutoSettings(s => ({...s, birthday_value: parseFloat(e.target.value) || 0}))} className="mt-1" data-testid="birthday-value" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Nachricht an den Kunden</Label>
                <textarea value={autoSettings.birthday_message} onChange={e => setAutoSettings(s => ({...s, birthday_message: e.target.value}))} className="mt-1 w-full min-h-[60px] p-2 border rounded-md text-sm resize-none" data-testid="birthday-message" />
              </div>
            </CardContent>
          )}
        </Card>

        {/* Loyalty Discount */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Heart className="w-5 h-5 text-red-500" />
                Treue-Rabatt
              </CardTitle>
              <Switch checked={autoSettings.loyalty_enabled} onCheckedChange={v => setAutoSettings(s => ({...s, loyalty_enabled: v}))} data-testid="loyalty-enabled" />
            </div>
          </CardHeader>
          {autoSettings.loyalty_enabled && (
            <CardContent className="space-y-3 pt-0">
              <p className="text-sm text-gray-500">Nach einer bestimmten Anzahl abgeschlossener Aufträge erhält der Kunde automatisch einen Treue-Rabatt.</p>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Nach X Aufträgen</Label>
                  <Input type="number" min="1" value={autoSettings.loyalty_threshold} onChange={e => setAutoSettings(s => ({...s, loyalty_threshold: parseInt(e.target.value) || 5}))} className="mt-1" data-testid="loyalty-threshold" />
                </div>
                <div>
                  <Label className="text-xs">Typ</Label>
                  <Select value={autoSettings.loyalty_type} onValueChange={v => setAutoSettings(s => ({...s, loyalty_type: v}))}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">Prozent (%)</SelectItem>
                      <SelectItem value="fixed">Festbetrag (EUR)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Wert</Label>
                  <Input type="number" min="0" step="0.01" value={autoSettings.loyalty_value} onChange={e => setAutoSettings(s => ({...s, loyalty_value: parseFloat(e.target.value) || 0}))} className="mt-1" data-testid="loyalty-value" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Nachricht an den Kunden</Label>
                <textarea value={autoSettings.loyalty_message} onChange={e => setAutoSettings(s => ({...s, loyalty_message: e.target.value}))} className="mt-1 w-full min-h-[60px] p-2 border rounded-md text-sm resize-none" data-testid="loyalty-message" />
              </div>
            </CardContent>
          )}
        </Card>

        {/* Reactivation Discount */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <RotateCcw className="w-5 h-5 text-blue-500" />
                Reaktivierungs-Rabatt
              </CardTitle>
              <Switch checked={autoSettings.reactivation_enabled} onCheckedChange={v => setAutoSettings(s => ({...s, reactivation_enabled: v}))} data-testid="reactivation-enabled" />
            </div>
          </CardHeader>
          {autoSettings.reactivation_enabled && (
            <CardContent className="space-y-3 pt-0">
              <p className="text-sm text-gray-500">Wenn ein Kunde länger inaktiv war, erhält er automatisch einen Rabatt zur Reaktivierung.</p>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Inaktiv seit (Monate)</Label>
                  <Input type="number" min="1" value={autoSettings.reactivation_months} onChange={e => setAutoSettings(s => ({...s, reactivation_months: parseInt(e.target.value) || 6}))} className="mt-1" data-testid="reactivation-months" />
                </div>
                <div>
                  <Label className="text-xs">Typ</Label>
                  <Select value={autoSettings.reactivation_type} onValueChange={v => setAutoSettings(s => ({...s, reactivation_type: v}))}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">Prozent (%)</SelectItem>
                      <SelectItem value="fixed">Festbetrag (EUR)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Wert</Label>
                  <Input type="number" min="0" step="0.01" value={autoSettings.reactivation_value} onChange={e => setAutoSettings(s => ({...s, reactivation_value: parseFloat(e.target.value) || 0}))} className="mt-1" data-testid="reactivation-value" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Nachricht an den Kunden</Label>
                <textarea value={autoSettings.reactivation_message} onChange={e => setAutoSettings(s => ({...s, reactivation_message: e.target.value}))} className="mt-1 w-full min-h-[60px] p-2 border rounded-md text-sm resize-none" data-testid="reactivation-message" />
              </div>
            </CardContent>
          )}
        </Card>

        {/* Auto-Discount Log */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <History className="w-5 h-5 text-gray-500" />
              Versand-Protokoll
            </CardTitle>
          </CardHeader>
          <CardContent>
            {autoLog.length > 0 ? (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {autoLog.map((log, i) => (
                  <div key={i} className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg text-sm">
                    <div className="flex items-center gap-2">
                      {log.type === 'birthday' && <Cake className="w-4 h-4 text-pink-500" />}
                      {log.type === 'loyalty' && <Heart className="w-4 h-4 text-red-500" />}
                      {log.type === 'reactivation' && <RotateCcw className="w-4 h-4 text-blue-500" />}
                      <span className="text-gray-600">{log.customer_email}</span>
                      <Badge variant="outline" className="text-xs">{log.discount_code}</Badge>
                    </div>
                    <span className="text-xs text-gray-400">{log.created_at ? new Date(log.created_at).toLocaleDateString('de-DE') : ''}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">Noch keine automatischen Rabatte versendet</p>
            )}
          </CardContent>
        </Card>
      </TabsContent>

      </Tabs>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={(v) => { if (!v) { setShowDialog(false); resetForm(); }}}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingCode ? 'Rabattcode bearbeiten' : 'Neuer Rabattcode'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Code *</Label>
                <Input value={form.code} onChange={e => setForm(f => ({...f, code: e.target.value.toUpperCase()}))} placeholder="z.B. SOMMER10" className="font-mono" data-testid="input-code" />
              </div>
              <div>
                <Label>Name</Label>
                <Input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} placeholder="z.B. Sommer-Rabatt" data-testid="input-name" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Typ</Label>
                <Select value={form.type} onValueChange={v => setForm(f => ({...f, type: v}))}>
                  <SelectTrigger data-testid="select-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Prozent (%)</SelectItem>
                    <SelectItem value="fixed">Festbetrag (EUR)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Wert *</Label>
                <div className="relative">
                  <Input type="number" step="0.01" min="0" value={form.value} onChange={e => setForm(f => ({...f, value: parseFloat(e.target.value) || 0}))} data-testid="input-value" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                    {form.type === 'percentage' ? '%' : 'EUR'}
                  </span>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Verwendung</Label>
                <Select value={form.usage_type} onValueChange={v => setForm(f => ({...f, usage_type: v}))}>
                  <SelectTrigger data-testid="select-usage"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="single">Einmalig</SelectItem>
                    <SelectItem value="multi">Mehrfach</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Max. Nutzungen</Label>
                <Input type="number" min="1" value={form.max_uses || ''} onChange={e => setForm(f => ({...f, max_uses: parseInt(e.target.value) || null}))} placeholder="Unbegrenzt" data-testid="input-max-uses" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Gültig ab</Label>
                <Input type="date" value={form.valid_from} onChange={e => setForm(f => ({...f, valid_from: e.target.value}))} data-testid="input-valid-from" />
              </div>
              <div>
                <Label>Gültig bis</Label>
                <Input type="date" value={form.valid_until} onChange={e => setForm(f => ({...f, valid_until: e.target.value}))} data-testid="input-valid-until" />
              </div>
            </div>
            <div>
              <Label>Mindestbestellwert (EUR)</Label>
              <Input type="number" step="0.01" min="0" value={form.min_order_value} onChange={e => setForm(f => ({...f, min_order_value: parseFloat(e.target.value) || 0}))} data-testid="input-min-order" />
            </div>
            <div className="flex items-center justify-between border rounded-lg p-3">
              <Label className="cursor-pointer">Aktiv</Label>
              <Switch checked={form.active} onCheckedChange={v => setForm(f => ({...f, active: v}))} data-testid="switch-active" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowDialog(false); resetForm(); }}>Abbrechen</Button>
            <Button onClick={handleSave} className="bg-[#9B1B30] hover:bg-[#7D1526]" data-testid="save-discount-btn">
              {editingCode ? 'Speichern' : 'Erstellen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Card Generator Dialog */}
      <Dialog open={!!cardDialog} onOpenChange={v => { if (!v) setCardDialog(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Karte erstellen: {cardDialog?.code}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-500 mb-4">Wählen Sie das Karten-Design:</p>
          <div className="grid grid-cols-1 gap-3">
            <Button
              variant="outline"
              className="h-auto py-4 justify-start gap-3 hover:border-[#9B1B30] hover:bg-[#9B1B30]/5"
              onClick={() => generateCard(cardDialog.id, 'geschenk')}
              disabled={cardLoading}
              data-testid="card-geschenk"
            >
              <Gift className="w-6 h-6 text-[#9B1B30]" />
              <div className="text-left">
                <div className="font-semibold">Geschenkgutschein</div>
                <div className="text-xs text-gray-500">Elegantes Design mit rotem Akzent</div>
              </div>
            </Button>
            <Button
              variant="outline"
              className="h-auto py-4 justify-start gap-3 hover:border-blue-500 hover:bg-blue-50"
              onClick={() => generateCard(cardDialog.id, 'rabatt')}
              disabled={cardLoading}
              data-testid="card-rabatt"
            >
              <CreditCard className="w-6 h-6 text-blue-500" />
              <div className="text-left">
                <div className="font-semibold">Rabattkarte</div>
                <div className="text-xs text-gray-500">Business-Design in Blau</div>
              </div>
            </Button>
            <Button
              variant="outline"
              className="h-auto py-4 justify-start gap-3 hover:border-gray-700 hover:bg-gray-50"
              onClick={() => generateCard(cardDialog.id, 'vip')}
              disabled={cardLoading}
              data-testid="card-vip"
            >
              <Crown className="w-6 h-6 text-gray-700" />
              <div className="text-left">
                <div className="font-semibold">VIP-Karte</div>
                <div className="text-xs text-gray-500">Premium-Design in Dunkel</div>
              </div>
            </Button>
          </div>
          {cardLoading && <p className="text-center text-sm text-gray-500 mt-2">Karte wird erstellt...</p>}
        </DialogContent>
      </Dialog>

      {/* Detail/Redemptions Dialog */}
      <Dialog open={!!detailDialog} onOpenChange={v => { if (!v) setDetailDialog(null); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Details: {detailDialog?.code}</DialogTitle>
          </DialogHeader>
          {detailDialog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-gray-50 rounded-lg p-3">
                  <span className="text-gray-500">Typ</span>
                  <p className="font-semibold">{detailDialog.type === 'percentage' ? 'Prozent' : 'Festbetrag'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <span className="text-gray-500">Wert</span>
                  <p className="font-semibold">{detailDialog.type === 'percentage' ? `${detailDialog.value}%` : `${detailDialog.value.toFixed(2)} EUR`}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <span className="text-gray-500">Verwendung</span>
                  <p className="font-semibold">{detailDialog.usage_type === 'single' ? 'Einmalig' : 'Mehrfach'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <span className="text-gray-500">Eingelöst</span>
                  <p className="font-semibold">{detailDialog.used_count || 0}{detailDialog.max_uses ? ` / ${detailDialog.max_uses}` : ''}</p>
                </div>
              </div>
              {detailDialog.redemptions && detailDialog.redemptions.length > 0 && (
                <div>
                  <h4 className="font-semibold text-sm mb-2">Einlöse-Verlauf</h4>
                  <div className="max-h-48 overflow-y-auto space-y-2">
                    {detailDialog.redemptions.map((r, i) => (
                      <div key={i} className="bg-gray-50 rounded-lg p-2.5 text-sm flex justify-between">
                        <div>
                          <span className="font-medium">{r.customer_name || 'Unbekannt'}</span>
                          {r.document_number && <span className="text-gray-500 ml-2">({r.document_number})</span>}
                        </div>
                        <span className="text-gray-400 text-xs">{r.redeemed_at ? new Date(r.redeemed_at).toLocaleDateString('de-DE') : ''}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
